import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { formatDateTime, patrolStatusColors, formatStatus } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Route, 
  Play, 
  CheckCircle2,
  XCircle,
  AlertTriangle,
  MapPin,
  User,
  Clock,
  RefreshCw,
  Loader2,
  Flag,
  Target,
  Navigation,
  Camera,
  FileText,
  StopCircle,
  Pause,
  Square,
  WifiOff,
  Shield,
  Plus,
  Building2,
  Layers,
  ChevronRight,
  ChevronDown,
  Edit,
  Trash2,
  Crosshair,
  GripVertical
} from 'lucide-react';
import StopPatrolModal from '../components/StopPatrolModal';
import ReportIncidentModal from '../components/ReportIncidentModal';
import RouteCheckpointManager from '../components/RouteCheckpointManager';
import { pendingActions, syncService } from '../lib/offlineDb';

export default function PatrolsPage() {
  const { api, user, isOfficer, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [patrolRoutes, setPatrolRoutes] = useState([]);
  const [patrolRuns, setPatrolRuns] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [showIncidentDialog, setShowIncidentDialog] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState('');
  const [activePatrol, setActivePatrol] = useState(null);
  const [activePatrolDetails, setActivePatrolDetails] = useState(null);
  const [scanLoading, setScanLoading] = useState(null);
  const [complianceStats, setComplianceStats] = useState(null);
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [selectedCheckpoint, setSelectedCheckpoint] = useState(null);
  const [scanNotes, setScanNotes] = useState('');
  const [scanStatus, setScanStatus] = useState('SUCCESS');
  const [gpsLocation, setGpsLocation] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [patrolIncidents, setPatrolIncidents] = useState([]);
  
  // Photo upload state
  const [scanPhotos, setScanPhotos] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  
  // P0: Site filter for patrol view
  const [filterSiteId, setFilterSiteId] = useState(() => searchParams.get('site_id') || '');
  
  // Patrols v2: Locations & Sectors state
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [locations, setLocations] = useState([]);
  const [expandedLocations, setExpandedLocations] = useState({});
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [showSectorDialog, setShowSectorDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editingSector, setEditingSector] = useState(null);
  const [locationForm, setLocationForm] = useState({
    name: '', address: '', gps_lat: '', gps_lng: '', geofence_radius_m: 100, description: ''
  });
  const [sectorForm, setSectorForm] = useState({
    name: '', gps_lat: '', gps_lng: '', geofence_radius_m: 75, description: ''
  });
  const [selectedLocationForSector, setSelectedLocationForSector] = useState(null);

  // Create Route Dialog state
  const [showCreateRouteDialog, setShowCreateRouteDialog] = useState(false);
  const [createRouteForm, setCreateRouteForm] = useState({
    name: '', site_id: '', description: '', estimated_duration_minutes: 30, frequency: 'hourly'
  });
  const [routeSubmitting, setRouteSubmitting] = useState(false);
  const [deletingRouteId, setDeletingRouteId] = useState(null);
  
  // Checkpoint Manager state
  const [showCheckpointManager, setShowCheckpointManager] = useState(false);
  const [selectedRouteForCheckpoints, setSelectedRouteForCheckpoints] = useState(null);
  const [showNextStepDialog, setShowNextStepDialog] = useState(false);
  const [newlyCreatedRoute, setNewlyCreatedRoute] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // 1. Resolve active shift FIRST
      let shiftSiteId = null;

      try {
        const shiftRes = await api.get('/shifts/my-shifts');
        const shifts = Array.isArray(shiftRes.data) ? shiftRes.data : [];

        console.log('[PatrolsPage] All shifts:', shifts);

        // PRIORITY ORDER: active → accepted → pending
        const currentShift =
          shifts.find(s =>
            s.workflow_stage === 'started' ||
            s.workflow_stage === 'patrol_active' ||
            s.status === 'in_progress'
          ) ||
          shifts.find(s =>
            s.workflow_stage === 'accepted' ||
            s.workflow_stage === 'signed_on' ||
            s.workflow_stage === 'geofence_validated'
          ) ||
          shifts.find(s =>
            s.workflow_stage === 'pending_acceptance'
          ) ||
          null;

        console.log('[PatrolsPage] Selected currentShift:', currentShift);

        // FIX: use currentShift (not undefined "shift")
        const shiftSiteId =
          currentShift?.site_id ||
          currentShift?.site?.id ||
          currentShift?.siteId ||
          null;

        const shiftSiteName =
          currentShift?.site_name ||
          currentShift?.site?.name ||
          '';

        console.log('[PatrolsPage] shiftSiteId:', shiftSiteId);
        console.log('[PatrolsPage] shiftSiteName:', shiftSiteName);

        // Store for later use (important)
        setCurrentShift(currentShift);
        setShiftSiteId(shiftSiteId);
        setShiftSiteName(shiftSiteName);

      } catch (err) {
        console.error('Failed to fetch active shift:', err);
      }

      // 2. HARD STOP if no shift site
      if (!shiftSiteId) {
         setPatrolRoutes([]);
         setPatrolRuns([]);
         setError(null);
         return;
      }

      // 3. Force site filter
      const siteQuery = `&site_id=${shiftSiteId}`;

      // 4. Load patrol data ONLY for that site
      const [routesRes, runsRes] = await Promise.all([
        api.get(`/patrols/routes?is_active=true${siteQuery}`),
        api.get(`/patrols/runs?today_only=true&limit=50${siteQuery}`)
      ]);

      // 5. Set state
      setPatrolRoutes(Array.isArray(routesRes.data) ? routesRes.data : []);
      setPatrolRuns(Array.isArray(runsRes.data) ? runsRes.data : []);
      setError(null);

    } catch (err) {
      console.error('Error loading patrol data:', err);
      setError('Failed to load patrol data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Patrols v2: Fetch locations for selected site
  const fetchLocations = useCallback(async (siteId) => {
    if (!siteId) {
      setLocations([]);
      return;
    }
    
    try {
      setLocationsLoading(true);
      const res = await api.get(`/sites/${siteId}/locations?include_sectors=true`);
      setLocations(res.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to load locations');
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  }, [api]);

  // Patrols v2: Create/Update Location
  const handleSaveLocation = async () => {
    if (!selectedSiteId) {
      toast.error('Please select a site first');
      return;
    }
    
    if (!locationForm.name.trim()) {
      toast.error('Location name is required');
      return;
    }
    
    try {
      const payload = {
        name: locationForm.name.trim(),
        address: locationForm.address || null,
        gps_lat: locationForm.gps_lat ? parseFloat(locationForm.gps_lat) : null,
        gps_lng: locationForm.gps_lng ? parseFloat(locationForm.gps_lng) : null,
        geofence_radius_m: parseInt(locationForm.geofence_radius_m) || 100,
        description: locationForm.description || null,
      };
      
      if (editingLocation) {
        await api.put(`/locations/${editingLocation.id}`, payload);
        toast.success('Location updated');
      } else {
        await api.post(`/sites/${selectedSiteId}/locations`, payload);
        toast.success('Location created');
      }
      
      setShowLocationDialog(false);
      setEditingLocation(null);
      setLocationForm({ name: '', address: '', gps_lat: '', gps_lng: '', geofence_radius_m: 100, description: '' });
      fetchLocations(selectedSiteId);
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error(error.response?.data?.detail || 'Failed to save location');
    }
  };

  // Patrols v2: Delete Location
  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Delete this location and all its sectors?')) return;
    
    try {
      await api.delete(`/locations/${locationId}`);
      toast.success('Location deleted');
      fetchLocations(selectedSiteId);
    } catch (error) {
      toast.error('Failed to delete location');
    }
  };

  // Patrols v2: Create/Update Sector
  const handleSaveSector = async () => {
    if (!selectedLocationForSector) {
      toast.error('No location selected');
      return;
    }
    
    if (!sectorForm.name.trim()) {
      toast.error('Sector name is required');
      return;
    }
    
    try {
      const payload = {
        name: sectorForm.name.trim(),
        gps_lat: sectorForm.gps_lat ? parseFloat(sectorForm.gps_lat) : null,
        gps_lng: sectorForm.gps_lng ? parseFloat(sectorForm.gps_lng) : null,
        geofence_radius_m: parseInt(sectorForm.geofence_radius_m) || 75,
        description: sectorForm.description || null,
      };
      
      if (editingSector) {
        await api.put(`/patrol-sectors/${editingSector.id}`, payload);
        toast.success('Sector updated');
      } else {
        await api.post(`/locations/${selectedLocationForSector.id}/sectors`, payload);
        toast.success('Sector created');
      }
      
      setShowSectorDialog(false);
      setEditingSector(null);
      setSelectedLocationForSector(null);
      setSectorForm({ name: '', gps_lat: '', gps_lng: '', geofence_radius_m: 75, description: '' });
      fetchLocations(selectedSiteId);
    } catch (error) {
      console.error('Error saving sector:', error);
      toast.error(error.response?.data?.detail || 'Failed to save sector');
    }
  };

  // Patrols v2: Delete Sector
  const handleDeleteSector = async (sectorId) => {
    if (!window.confirm('Delete this sector?')) return;
    
    try {
      await api.delete(`/patrol-sectors/${sectorId}`);
      toast.success('Sector deleted');
      fetchLocations(selectedSiteId);
    } catch (error) {
      toast.error('Failed to delete sector');
    }
  };

  // Patrols v2: Toggle location expansion
  const toggleLocationExpanded = (locationId) => {
    setExpandedLocations(prev => ({
      ...prev,
      [locationId]: !prev[locationId]
    }));
  };

  // Patrols v2: Open edit dialogs
  const openEditLocation = (location) => {
    setEditingLocation(location);
    setLocationForm({
      name: location.name || '',
      address: location.address || '',
      gps_lat: location.gps_lat?.toString() || '',
      gps_lng: location.gps_lng?.toString() || '',
      geofence_radius_m: location.geofence_radius_m || 100,
      description: location.description || '',
    });
    setShowLocationDialog(true);
  };

  const openAddSector = (location) => {
    setSelectedLocationForSector(location);
    setEditingSector(null);
    setSectorForm({ name: '', gps_lat: '', gps_lng: '', geofence_radius_m: 75, description: '' });
    setShowSectorDialog(true);
  };

  const openEditSector = (sector, location) => {
    setSelectedLocationForSector(location);
    setEditingSector(sector);
    setSectorForm({
      name: sector.name || '',
      gps_lat: sector.gps_lat?.toString() || '',
      gps_lng: sector.gps_lng?.toString() || '',
      geofence_radius_m: sector.geofence_radius_m || 75,
      description: sector.description || '',
    });
    setShowSectorDialog(true);
  };

  // Create Patrol Route
  const handleCreateRoute = async () => {
    if (!createRouteForm.name.trim()) {
      toast.error('Route name is required');
      return;
    }
    if (!createRouteForm.site_id) {
      toast.error('Please select a site');
      return;
    }
    
    setRouteSubmitting(true);
    try {
      const response = await api.post('/patrols/routes', {
        name: createRouteForm.name.trim(),
        site_id: createRouteForm.site_id,
        description: createRouteForm.description || null,
        estimated_duration_minutes: parseInt(createRouteForm.estimated_duration_minutes) || 30,
        frequency: createRouteForm.frequency || 'hourly',
        checkpoint_ids: []  // Empty initially - add via checkpoint manager
      });
      toast.success('Patrol route created');
      setShowCreateRouteDialog(false);
      setCreateRouteForm({ name: '', site_id: '', description: '', estimated_duration_minutes: 30, frequency: 'hourly' });
      
      // Show "Next: Add Checkpoints" prompt
      setNewlyCreatedRoute(response.data);
      setShowNextStepDialog(true);
      
      fetchData();
    } catch (error) {
      console.error('Error creating route:', error);
      toast.error(error.response?.data?.detail || 'Failed to create route');
    } finally {
      setRouteSubmitting(false);
    }
  };

  // Open checkpoint manager for a route
  const openCheckpointManager = (route) => {
    setSelectedRouteForCheckpoints(route);
    setShowCheckpointManager(true);
  };

  // Delete Patrol Route
  const handleDeleteRoute = async (routeId, routeName) => {
    if (!window.confirm(`Delete patrol route "${routeName}"? This action cannot be undone.`)) return;
    
    setDeletingRouteId(routeId);
    try {
      await api.delete(`/patrols/routes/${routeId}`);
      toast.success('Patrol route deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting route:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete route');
    } finally {
      setDeletingRouteId(null);
    }
  };

  // Patrols v2: Use GPS for forms
  const handleUseGpsForLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }
    
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 });
      });
      setLocationForm(prev => ({
        ...prev,
        gps_lat: position.coords.latitude.toFixed(6),
        gps_lng: position.coords.longitude.toFixed(6),
      }));
      toast.success('GPS coordinates captured');
    } catch (error) {
      toast.error('Failed to get GPS location');
    }
  };

  const handleUseGpsForSector = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }
    
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 });
      });
      setSectorForm(prev => ({
        ...prev,
        gps_lat: position.coords.latitude.toFixed(6),
        gps_lng: position.coords.longitude.toFixed(6),
      }));
      toast.success('GPS coordinates captured');
    } catch (error) {
      toast.error('Failed to get GPS location');
    }
  };

  // Monitor online status and sync pending actions
  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      toast.success('Back online');
      
      // Attempt to sync pending actions
      const count = await pendingActions.getPendingCount();
      if (count > 0) {
        toast.info(`Syncing ${count} pending actions...`);
        const result = await syncService.syncAll(api);
        if (result.success) {
          toast.success('All actions synced');
          fetchData();
        }
      }
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('You are offline. Changes will sync when connected.', {
        icon: <WifiOff className="w-4 h-4" />,
      });
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Check pending count on mount
    pendingActions.getPendingCount().then(setPendingCount);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [api, fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStartPatrol = async () => {
    if (!selectedRoute) {
      toast.error('Please select a patrol route');
      return;
    }
    
    // P0 FIX: Find the selected route and validate it has checkpoints
    const route = patrolRoutes?.find(r => r.id === selectedRoute);
    if (route) {
      const checkpointCount = (route.checkpoint_ids?.length || 0) + (route.sectors?.length || 0) + (route.stops?.length || 0) + (route.checkpoint_count || 0);
      if (checkpointCount === 0) {
        toast.error('This route has no checkpoints. Please add checkpoints in Route Builder first.');
        return;
      }
    }
    
    try {
      const response = await api.post(`/patrols/runs?patrol_route_id=${selectedRoute}`);
      
      // P0 FIX: Validate response structure
      if (!response?.data?.id) {
        console.error('[StartPatrol] Invalid response:', response?.data);
        toast.error('Failed to start patrol - invalid response from server');
        return;
      }
      
      toast.success(`Patrol started: ${response.data.route_name || 'Patrol'}`);
      setShowStartDialog(false);
      setSelectedRoute('');
      fetchData();
    } catch (error) {
      console.error('[StartPatrol] Error:', error);
      // P0 FIX: Show specific error message from backend
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to start patrol';
      toast.error(errorMessage);
    }
  };

  const handlePatrolStopped = (stoppedPatrol) => {
    setActivePatrol(null);
    setActivePatrolDetails(null);
    setShowStopDialog(false);
    fetchData();
  };

  const handlePausePatrol = async () => {
    if (!activePatrol) return;
    
    try {
      await api.post(`/patrols/runs/${activePatrol.id}/pause`);
      toast.success('Patrol paused');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pause patrol');
    }
  };

  const handleResumePatrol = async () => {
    if (!activePatrol) return;
    
    try {
      await api.post(`/patrols/runs/${activePatrol.id}/resume`);
      toast.success('Patrol resumed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resume patrol');
    }
  };

  const requestGPS = () => {
    setGpsLoading(true);
    
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported');
      setGpsLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setGpsLoading(false);
        toast.success('Location captured');
      },
      (error) => {
        toast.error('Failed to get location');
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const openScanDialog = (checkpoint) => {
    setSelectedCheckpoint(checkpoint);
    setScanNotes('');
    setScanStatus('SUCCESS');
    setGpsLocation(null);
    setScanPhotos([]);
    setShowScanDialog(true);
    
    // P0 FIX: Auto-capture GPS when opening Check In dialog
    if (navigator.geolocation) {
      setGpsLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
          setGpsLoading(false);
        },
        (error) => {
          console.warn('Auto GPS capture failed:', error);
          setGpsLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  };

  // Photo upload handler
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Limit to 2 photos
    const newPhotos = files.slice(0, 2 - scanPhotos.length);
    if (newPhotos.length === 0) {
      toast.error('Maximum 2 photos allowed');
      return;
    }
    
    setPhotoUploading(true);
    const uploadedPhotos = [];
    
    for (const file of newPhotos) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'checkpoint_scan');
        
        // FIX: Use correct endpoint /files/photo instead of /uploads/photo
        const res = await api.post('/files/photo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedPhotos.push({
          url: res.data.url,
          filename: res.data.filename,
          uploaded_at: new Date().toISOString()
        });
        toast.success(`Photo uploaded: ${file.name}`);
      } catch (error) {
        console.error('Error uploading photo:', error);
        const errorMsg = error.response?.data?.detail || 'Upload failed';
        toast.error(`Failed to upload ${file.name}: ${errorMsg}`);
      }
    }
    
    setScanPhotos(prev => [...prev, ...uploadedPhotos]);
    setPhotoUploading(false);
    
    // Clear input
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setScanPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleScanCheckpoint = async () => {
    if (!activePatrol || !selectedCheckpoint) return;
    
    const scanData = {
      patrol_id: activePatrol.id,
      checkpoint_id: selectedCheckpoint.id,
      result: scanStatus,
      notes: scanNotes,
      latitude: gpsLocation?.latitude || null,
      longitude: gpsLocation?.longitude || null,
      timestamp: new Date().toISOString(),
      photos: scanPhotos, // Include uploaded photos
    };

    // If offline, queue for later sync
    if (!isOnline) {
      try {
        await pendingActions.queueCheckpoint(scanData);
        toast.success(`Checkpoint saved offline - will sync when online`);
        setShowScanDialog(false);
        setSelectedCheckpoint(null);
        setScanNotes('');
        
        // Update pending count
        const count = await pendingActions.getPendingCount();
        setPendingCount(count);
      } catch (error) {
        console.error('Error saving offline checkpoint:', error);
        toast.error('Failed to save checkpoint offline');
      } finally {
        setScanLoading(null);
      }
      return;
    }
    
    try {
      setScanLoading(selectedCheckpoint.id);
      
      let url = `/patrols/runs/${activePatrol.id}/scan?checkpoint_id=${selectedCheckpoint.id}&result_status=${scanStatus}`;
      if (gpsLocation) {
        url += `&latitude=${gpsLocation.latitude}&longitude=${gpsLocation.longitude}`;
      }
      if (scanNotes) {
        url += `&notes=${encodeURIComponent(scanNotes)}`;
      }

      const response = await api.post(url);
      
      if (response.data.exception_reason) {
        toast.warning(`Checkpoint scanned with exception: ${response.data.exception_reason}`);
      } else {
        toast.success(`Checkpoint "${selectedCheckpoint.name}" scanned successfully`);
      }
      
      setShowScanDialog(false);
      setSelectedCheckpoint(null);
      setScanNotes('');
      fetchData();
    } catch (error) {
      console.error('Error scanning checkpoint:', error);
      
      // If network error, fall back to offline queue
      if (!navigator.onLine){
        try {
          await pendingActions.queueCheckpoint(scanData);
          toast.warning('Network unavailable - checkpoint saved offline');
          setShowScanDialog(false);
          setSelectedCheckpoint(null);
          setScanNotes('');
          
          const count = await pendingActions.getPendingCount();
          setPendingCount(count);
        } catch (offlineError) {
          toast.error('Failed to save checkpoint');
        }
      } else {
        toast.error(error.response?.data?.detail || 'Failed to scan checkpoint');
      }
    } finally {
      setScanLoading(null);
    }
  };

  const handleCompletePatrol = async () => {
    if (!activePatrol) return;
    
    try {
      const response = await api.post(`/patrols/runs/${activePatrol.id}/complete`);
      toast.success(
        <div className="space-y-2">
          <p>Patrol completed: {response.data.checkpoints_completed}/{response.data.total_checkpoints} checkpoints</p>
          <div className="flex gap-2 mt-2">
            <button
              onClick={async () => {
                try {
                  toast.info('Generating Report...', { duration: 2000 });
                  const pdfRes = await api.get(`/patrols/runs/${activePatrol.id}/enterprise-report`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([pdfRes.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `RX_Patrol_Report_${activePatrol.id}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success('Report downloaded');
                } catch (e) {
                  console.error('PDF download error:', e);
                  toast.error('Failed to generate report');
                }
              }}
              className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
            >
              Download Report
            </button>
            <button
              onClick={async () => {
                try {
                  toast.info('Generating Enterprise Report...', { duration: 2000 });
                  const pdfRes = await api.get(`/patrols/runs/${activePatrol.id}/enterprise-report`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([pdfRes.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `RX_Patrol_Report_${activePatrol.id}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success('Enterprise Report downloaded');
                } catch (e) {
                  console.error('PDF download error:', e);
                  toast.error('Failed to generate report');
                }
              }}
              className="px-3 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-500"
            >
              Enterprise Report
            </button>
          </div>
        </div>,
        { duration: 10000 }
      );
      fetchData();
    } catch (error) {
      console.error('Error completing patrol:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete patrol');
    }
  };

  const [syncing, setSyncing] = useState(false);

  const handleManualSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const result = await syncService.syncAll(api);
      if (result.success) {
        const total = (result.results?.checkpoints?.synced || 0) + 
                     (result.results?.incidents?.synced || 0) + 
                     (result.results?.stops?.synced || 0);
        toast.success(`Synced ${total} actions successfully`);
        fetchData();
      } else {
        toast.error(result.error || 'Sync failed');
      }
    } catch (err) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
      const count = await pendingActions.getPendingCount();
      setPendingCount(count);
    }
  };

  const getRouteCheckpoints = (route) => {
    // New format: use stops array with sector_name/sector_id
    if (route?.stops && route.stops.length > 0) {
      return route.stops.map((stop, idx) => ({
        id: stop.sector_id || stop.id || `stop-${idx}`,
        name: stop.sector_name || stop.name || `Checkpoint ${idx + 1}`,
        order: stop.order || idx + 1,
        gps_lat: stop.gps_lat,
        gps_lng: stop.gps_lng,
        geofence_radius_m: stop.geofence_radius_m || 50
      }));
    }
    
    // Legacy format: use checkpoint_ids array
    if (!route?.checkpoint_ids || route.checkpoint_ids.length === 0) return [];
    const site = sites.find(s => s.id === route.site_id);
    if (!site?.checkpoints) return [];
    
    return route.checkpoint_ids.map(cpId => {
      const cp = site.checkpoints.find(c => c.id === cpId);
      return cp || { id: cpId, name: `Checkpoint ${cpId.slice(0, 8)}` };
    });
  };

  const getScannedCheckpoints = () => {
    return activePatrolDetails?.checkpoint_scans?.map(s => s.checkpoint_id) || [];
  };

  return (
    <div className="space-y-6" data-testid="patrols-page">
      {/* Offline/Pending Banner */}
      {(!isOnline || pendingCount > 0) && (
        <div className={`flex items-center gap-2 p-3 ${!isOnline ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-blue-500/10 border-blue-500/30'} border rounded-sm`}>
          <WifiOff className={`w-4 h-4 ${!isOnline ? 'text-yellow-400' : 'text-blue-400'}`} />
          <span className={`text-sm ${!isOnline ? 'text-yellow-400' : 'text-blue-400'}`}>
            {!isOnline 
              ? "You're offline. Changes will sync when you're back online."
              : `${pendingCount} pending action(s) to sync.`
            }
          </span>
          {pendingCount > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <Badge className={`${!isOnline ? 'bg-yellow-500/20 text-yellow-400' : 'bg-blue-500/20 text-blue-400'}`}>
                {pendingCount} pending
              </Badge>
              {isOnline && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleManualSync}
                  disabled={syncing}
                  className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                  data-testid="sync-btn"
                >
                  {syncing ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Sync Now
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white tracking-wide">
            PATROL MANAGEMENT
          </h1>
          <p className="text-sm text-zinc-500">Execute and monitor patrol rounds</p>
        </div>
        
        <div className="flex gap-2 flex-wrap items-center">
          {/* P0: Site Filter Selector */}
          <Select 
            value={filterSiteId || "all"} 
            onValueChange={(v) => {
              const newValue = v === "all" ? "" : v;
              setFilterSiteId(newValue);
              setSearchParams(prev => {
                const params = new URLSearchParams(prev);
                if (newValue) {
                  params.set('site_id', newValue);
                } else {
                  params.delete('site_id');
                }
                return params;
              }, { replace: true });
            }}
          >
            <SelectTrigger className="w-[200px] bg-[#09090b] border-[#3f3f46]" data-testid="site-filter-select">
              <Building2 className="w-4 h-4 mr-2 text-zinc-500" />
              <SelectValue placeholder="All Sites" />
            </SelectTrigger>
            <SelectContent className="bg-[#18181b] border-[#3f3f46]">
              <SelectItem value="all">All Sites</SelectItem>
              {sites.map(site => (
                <SelectItem key={site.id} value={site.id}>
                  {site.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            onClick={fetchData}
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            REFRESH
          </Button>
          
          {isOfficer && !activePatrol && (
            <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-rajdhani font-semibold rounded-sm shadow-[0_0_10px_rgba(16,185,129,0.3)]"
                  data-testid="start-patrol-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  START PATROL
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-rajdhani text-xl">START PATROL RUN</DialogTitle>
                  <DialogDescription className="text-zinc-500">
                    Select a patrol route to begin your patrol round.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Select Route</Label>
                    <Select value={selectedRoute} onValueChange={setSelectedRoute}>
                      <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="patrol-route-select">
                        <SelectValue placeholder="Choose a patrol route" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                        {patrolRoutes.filter(r => {
                          // Only show routes for mobile sites
                          const site = sites.find(s => s.id === r.site_id);
                          return r.is_active && (!site || (site.site_type || 'mobile') === 'mobile');
                        }).map(route => (
                          <SelectItem key={route.id} value={route.id}>
                            {route.name} - {route.site_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {selectedRoute && (() => {
                    const route = patrolRoutes.find(r => r.id === selectedRoute);
                    return route ? (
                      <div className="p-3 bg-[#09090b] rounded border border-[#3f3f46] space-y-2">
                        <div className="flex items-center gap-2 text-sm text-zinc-300">
                          <MapPin className="w-4 h-4 text-zinc-500" />
                          {route.site_name}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-zinc-400">
                          <span><strong>{route.checkpoint_count}</strong> checkpoints</span>
                          <span>Est. <strong>{route.estimated_duration_minutes}</strong> mins</span>
                        </div>
                        {route.description && (
                          <p className="text-xs text-zinc-500">{route.description}</p>
                        )}
                      </div>
                    ) : null;
                  })()}
                </div>
                <DialogFooter className="gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowStartDialog(false)}
                    className="border-zinc-600"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleStartPatrol}
                    className="bg-emerald-500 hover:bg-emerald-600"
                    disabled={!selectedRoute}
                    data-testid="confirm-start-patrol-btn"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start Patrol
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Compliance Stats (Admin) */}
      {isAdmin && complianceStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Route className="w-5 h-5 text-blue-400" />
                <span className="text-2xl font-mono font-bold text-zinc-300">
                  {complianceStats.total_patrols || 0}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">Total Patrols</p>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <span className="text-2xl font-mono font-bold text-emerald-400">
                  {complianceStats.completed_patrols || 0}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">Completed</p>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
                <span className={`text-2xl font-mono font-bold ${complianceStats.with_exceptions > 0 ? 'text-orange-400' : 'text-zinc-400'}`}>
                  {complianceStats.with_exceptions || 0}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">With Exceptions</p>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Target className="w-5 h-5 text-emerald-400" />
                <span className={`text-2xl font-mono font-bold ${complianceStats.compliance_rate >= 90 ? 'text-emerald-400' : 'text-yellow-400'}`}>
                  {complianceStats.compliance_rate?.toFixed(0) || 0}%
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">Compliance Rate</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Active Patrol */}
      {activePatrol && activePatrolDetails && (
        <Card className="bg-[#18181b] border-emerald-500/50 shadow-[0_0_15px_rgba(16,185,129,0.1)]">
          <CardHeader className="border-b border-[#3f3f46] bg-emerald-500/10 py-3 px-4">
            <CardTitle className="text-sm font-rajdhani font-semibold text-emerald-400 uppercase tracking-wider flex items-center gap-2">
              <Play className="w-4 h-4 animate-pulse" />
              ACTIVE PATROL
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
              <div>
                <h3 className="text-lg font-rajdhani text-white">{activePatrolDetails.route_name}</h3>
                <div className="flex items-center gap-3 text-sm text-zinc-400">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {activePatrolDetails.site_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Started {formatDateTime(activePatrolDetails.started_at)}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowIncidentDialog(true)}
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  data-testid="report-incident-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Report Incident
                </Button>
                <Button
                  onClick={handleCompletePatrol}
                  className="bg-blue-500 hover:bg-blue-600"
                  data-testid="complete-patrol-btn"
                >
                  <StopCircle className="w-4 h-4 mr-2" />
                  Complete Patrol
                </Button>
              </div>
            </div>
            
            {/* Linked Incidents Banner */}
            {patrolIncidents.length > 0 && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-red-400" />
                  <span className="text-sm font-medium text-red-400">
                    {patrolIncidents.length} Incident(s) Reported
                  </span>
                </div>
                <div className="space-y-1">
                  {patrolIncidents.map(incident => (
                    <div key={incident.id} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <Badge className={`text-xs ${
                          incident.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                          incident.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                          incident.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {incident.severity?.toUpperCase()}
                        </Badge>
                        <span className="text-zinc-300">{incident.ir_number}</span>
                        <span className="text-zinc-500">{incident.title}</span>
                      </div>
                      {incident.photos_count > 0 && (
                        <span className="text-zinc-500 flex items-center gap-1">
                          <Camera className="w-3 h-3" />
                          {incident.photos_count}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Progress */}
            <div className="mb-6">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-zinc-400">Progress</span>
                <span className="font-mono">
                  <span className="text-emerald-400">{activePatrolDetails.checkpoints_completed}</span>
                  <span className="text-zinc-500"> / </span>
                  <span className="text-zinc-300">{activePatrolDetails.total_checkpoints}</span>
                </span>
              </div>
              <Progress 
                value={activePatrolDetails.completion_percentage} 
                className="h-2 bg-[#27272a]"
              />
              {activePatrolDetails.has_exceptions && (
                <p className="text-xs text-orange-400 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Patrol has exceptions
                </p>
              )}
            </div>
            
            {/* Checkpoint List */}
            <div className="space-y-2">
              <h4 className="text-xs font-rajdhani font-semibold text-zinc-400 uppercase tracking-wider mb-3">
                Checkpoints
              </h4>
              {(() => {
                const route = patrolRoutes.find(r => r.id === activePatrolDetails.patrol_route_id);
                const checkpoints = getRouteCheckpoints(route);
                const scannedIds = getScannedCheckpoints();
                
                return checkpoints.map((cp, idx) => {
                  const scanned = scannedIds.includes(cp.id);
                  const scanResult = activePatrolDetails.checkpoint_scans?.find(s => s.checkpoint_id === cp.id);
                  const isException = scanResult && scanResult.result !== 'SUCCESS';
                  
                  return (
                    <div 
                      key={cp.id}
                      className={`flex items-center justify-between p-3 rounded border transition-colors ${
                        scanned 
                          ? isException
                            ? 'bg-orange-500/10 border-orange-500/30'
                            : 'bg-emerald-500/10 border-emerald-500/30'
                          : 'bg-[#09090b] border-[#3f3f46] hover:border-zinc-500'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className={`text-xs font-mono w-6 ${scanned ? 'text-zinc-500' : 'text-zinc-400'}`}>
                          {idx + 1}.
                        </span>
                        <div className="flex-1 min-w-0">
                          <span className={`text-sm block truncate ${scanned ? 'text-zinc-400' : 'text-zinc-200'}`}>
                            {cp.name}
                          </span>
                          {scanResult && (
                            <span className="text-xs text-zinc-500">
                              Scanned {formatDateTime(scanResult.scanned_at)}
                            </span>
                          )}
                        </div>
                        {scanned && (
                          <Badge className={`text-xs flex-shrink-0 ${
                            isException
                              ? 'bg-orange-500/20 text-orange-400 border-orange-500/50' 
                              : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50'
                          }`}>
                            {scanResult?.result}
                          </Badge>
                        )}
                      </div>
                      {!scanned && (
                        <Button
                          size="sm"
                          className="bg-blue-500 hover:bg-blue-600 ml-2 flex-shrink-0"
                          onClick={() => openScanDialog(cp)}
                          disabled={scanLoading === cp.id}
                          data-testid={`scan-checkpoint-${cp.id}`}
                        >
                          {scanLoading === cp.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Target className="w-4 h-4 mr-1" />
                              Check In
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs for Routes and Runs */}
      <Tabs defaultValue="runs" className="w-full">
        <TabsList className="bg-[#27272a] border-b border-[#3f3f46] w-full justify-start rounded-none h-auto p-0">
          <TabsTrigger 
            value="runs" 
            className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-500 px-4 py-2"
          >
            <Clock className="w-4 h-4 mr-2" />
            Today's Patrols
          </TabsTrigger>
          <TabsTrigger 
            value="routes" 
            className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-500 px-4 py-2"
          >
            <Route className="w-4 h-4 mr-2" />
            Patrol Routes
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger 
              value="locations" 
              className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-emerald-500 px-4 py-2"
              data-testid="locations-tab"
            >
              <Layers className="w-4 h-4 mr-2" />
              Locations & Sectors
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="runs" className="mt-4">
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardHeader className="border-b border-[#3f3f46] bg-[#18181b]/50 py-3 px-4">
              <CardTitle className="text-sm font-rajdhani font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
                <Clock className="w-4 h-4 text-yellow-400" />
                TODAY'S PATROL RUNS ({patrolRuns.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
                </div>
              ) : patrolRuns.length === 0 ? (
                <div className="p-8 text-center text-zinc-500">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No patrol runs today</p>
                </div>
              ) : (
                <div className="divide-y divide-[#3f3f46]">
                  {patrolRuns.map((run) => (
                    <div 
                      key={run.id}
                      className="p-4 hover:bg-[#27272a]/50 transition-colors"
                      data-testid={`run-item-${run.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge className={`${patrolStatusColors[run.status?.toLowerCase()]} text-xs px-1.5 py-0 rounded-sm border`}>
                              {formatStatus(run.status)}
                            </Badge>
                            {run.has_exceptions && (
                              <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/50 text-xs">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Exceptions
                              </Badge>
                            )}
                          </div>
                          <h3 className="text-sm font-medium text-zinc-200">{run.route_name}</h3>
                          <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500 flex-wrap">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {run.site_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {run.officer_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDateTime(run.started_at)}
                            </span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="flex items-center gap-1 justify-end">
                            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                            <span className="text-sm font-mono">
                              <span className="text-emerald-400">{run.checkpoints_completed}</span>
                              <span className="text-zinc-500">/</span>
                              <span className="text-zinc-400">{run.total_checkpoints}</span>
                            </span>
                          </div>
                          {run.checkpoints_failed > 0 && (
                            <div className="flex items-center gap-1 mt-1 justify-end">
                              <XCircle className="w-3 h-3 text-red-400" />
                              <span className="text-xs text-red-400">{run.checkpoints_failed} failed</span>
                            </div>
                          )}
                          <div className="text-xs text-zinc-500 mt-1">
                            {run.completion_percentage}%
                          </div>
                          {/* Enterprise Report Button for completed patrols */}
                          {run.status === 'completed' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="mt-2 text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 p-1 h-auto"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  toast.info('Generating Enterprise Report...', { duration: 3000 });
                                  const pdfRes = await api.get(`/patrols/runs/${run.id}/enterprise-report`, { responseType: 'blob' });
                                  const url = window.URL.createObjectURL(new Blob([pdfRes.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', `RX_Enterprise_Report_${run.id}.pdf`);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                  toast.success('Enterprise Report downloaded');
                                } catch (err) {
                                  console.error('Report error:', err);
                                  toast.error('Failed to generate report');
                                }
                              }}
                              data-testid={`enterprise-report-btn-${run.id}`}
                            >
                              <FileText className="w-3 h-3 mr-1" />
                              Enterprise Report
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="routes" className="mt-4">
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardHeader className="border-b border-[#3f3f46] bg-[#18181b]/50 py-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-rajdhani font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
                  <Route className="w-4 h-4 text-blue-400" />
                  PATROL ROUTES ({patrolRoutes.length})
                </CardTitle>
                {isAdmin && (
                  <Button
                    size="sm"
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                    onClick={() => setShowCreateRouteDialog(true)}
                    data-testid="create-route-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    New Route
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
                </div>
              ) : patrolRoutes.length === 0 ? (
                <div className="p-8 text-center text-zinc-500">
                  <Route className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm font-medium">
                    {filterSiteId 
                      ? `No patrol routes configured for this site`
                      : `No patrol routes defined`
                    }
                  </p>
                  <p className="text-xs mt-1">
                    {filterSiteId 
                      ? `Create a patrol route or assign an existing route to this site`
                      : `Get started by creating your first patrol route`
                    }
                  </p>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4 border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                      onClick={() => setShowCreateRouteDialog(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Route
                    </Button>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-[#3f3f46]">
                  {patrolRoutes.map((route) => (
                    <div 
                      key={route.id}
                      className="p-4 hover:bg-[#27272a]/50 transition-colors"
                      data-testid={`route-item-${route.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-medium text-zinc-200">{route.name}</h3>
                            {(route.checkpoint_count === 0 || !route.checkpoint_count) && (
                              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50 text-xs">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                No checkpoints
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500 flex-wrap">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {route.site_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              {route.checkpoint_count || 0} checkpoints
                            </span>
                            <span>{route.estimated_duration_minutes || 30} mins</span>
                            <span className="capitalize">{route.frequency || 'hourly'}</span>
                          </div>
                          {route.description && (
                            <p className="text-xs text-zinc-500 mt-2">{route.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`text-xs ${
                            route.checkpoint_count > 0 && route.is_active 
                              ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50' 
                              : 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50'
                          }`}>
                            {route.checkpoint_count > 0 && route.is_active ? 'Active' : 'Draft'}
                          </Badge>
                          {isAdmin && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                                onClick={() => openCheckpointManager(route)}
                                data-testid={`manage-checkpoints-btn-${route.id}`}
                              >
                                <Layers className="w-3 h-3 mr-1" />
                                Checkpoints
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                onClick={() => handleDeleteRoute(route.id, route.name)}
                                disabled={deletingRouteId === route.id}
                                data-testid={`delete-route-btn-${route.id}`}
                              >
                                {deletingRouteId === route.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Patrols v2: Locations & Sectors Tab */}
        {isAdmin && (
          <TabsContent value="locations" className="mt-4">
            <div className="space-y-4">
              {/* Site Selector */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px] max-w-xs">
                  <Select 
                    value={selectedSiteId} 
                    onValueChange={(v) => {
                      setSelectedSiteId(v);
                      fetchLocations(v);
                    }}
                  >
                    <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                      <Building2 className="w-4 h-4 mr-2 text-zinc-500" />
                      <SelectValue placeholder="Select a site" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                      {sites.map(site => (
                        <SelectItem key={site.id} value={site.id}>
                          {site.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedSiteId && (
                  <Button
                    onClick={() => {
                      setEditingLocation(null);
                      setLocationForm({ name: '', address: '', gps_lat: '', gps_lng: '', geofence_radius_m: 100, description: '' });
                      setShowLocationDialog(true);
                    }}
                    className="bg-emerald-500 hover:bg-emerald-600"
                    data-testid="add-location-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Location
                  </Button>
                )}
              </div>

              {/* Locations List */}
              {!selectedSiteId ? (
                <Card className="bg-[#18181b] border-[#3f3f46]">
                  <CardContent className="p-8 text-center text-zinc-500">
                    <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Select a site to manage locations and sectors</p>
                  </CardContent>
                </Card>
              ) : locationsLoading ? (
                <Card className="bg-[#18181b] border-[#3f3f46]">
                  <CardContent className="p-8 text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-emerald-500" />
                  </CardContent>
                </Card>
              ) : locations.length === 0 ? (
                <Card className="bg-[#18181b] border-[#3f3f46]">
                  <CardContent className="p-8 text-center text-zinc-500">
                    <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No locations defined for this site</p>
                    <p className="text-xs mt-1">Click "Add Location" to create one</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {locations.map(location => (
                    <Card 
                      key={location.id} 
                      className="bg-[#18181b] border-[#3f3f46] overflow-hidden"
                      data-testid={`location-card-${location.id}`}
                    >
                      {/* Location Header */}
                      <div 
                        className="p-4 flex items-center gap-3 cursor-pointer hover:bg-[#27272a]/50 transition-colors"
                        onClick={() => toggleLocationExpanded(location.id)}
                      >
                        <button className="text-zinc-500 hover:text-zinc-300">
                          {expandedLocations[location.id] ? (
                            <ChevronDown className="w-5 h-5" />
                          ) : (
                            <ChevronRight className="w-5 h-5" />
                          )}
                        </button>
                        <Building2 className="w-5 h-5 text-emerald-400" />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-zinc-200">{location.name}</h3>
                          <div className="flex items-center gap-3 text-xs text-zinc-500 mt-0.5 flex-wrap">
                            {location.address && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {location.address}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Layers className="w-3 h-3" />
                              {location.sector_count || 0} sectors
                            </span>
                            {location.gps_lat && location.gps_lng && (
                              <span className="text-emerald-400">GPS Set</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openAddSector(location)}
                            className="text-blue-400 hover:text-blue-300 h-8"
                            data-testid={`add-sector-btn-${location.id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditLocation(location)}
                            className="text-zinc-400 hover:text-zinc-300 h-8"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteLocation(location.id)}
                            className="text-red-400 hover:text-red-300 h-8"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Sectors List (Expandable) */}
                      {expandedLocations[location.id] && (
                        <div className="border-t border-[#3f3f46] bg-[#09090b]">
                          {location.sectors?.length > 0 ? (
                            <div className="divide-y divide-[#27272a]">
                              {location.sectors.map((sector, idx) => (
                                <div 
                                  key={sector.id}
                                  className="p-3 pl-12 flex items-center gap-3 hover:bg-[#18181b] transition-colors"
                                  data-testid={`sector-item-${sector.id}`}
                                >
                                  <GripVertical className="w-4 h-4 text-zinc-600" />
                                  <span className="text-xs font-mono text-zinc-500 w-6">{sector.visit_order || idx + 1}.</span>
                                  <Target className="w-4 h-4 text-blue-400" />
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm text-zinc-300">{sector.name}</span>
                                    {sector.gps_lat && sector.gps_lng && (
                                      <span className="ml-2 text-xs text-emerald-400">GPS Set</span>
                                    )}
                                    {sector.description && (
                                      <p className="text-xs text-zinc-500 mt-0.5">{sector.description}</p>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openEditSector(sector, location)}
                                      className="text-zinc-400 hover:text-zinc-300 h-7 px-2"
                                    >
                                      <Edit className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteSector(sector.id)}
                                      className="text-red-400 hover:text-red-300 h-7 px-2"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="p-4 pl-12 text-center text-zinc-500 text-sm">
                              No sectors defined. Click <Plus className="w-3 h-3 inline" /> to add one.
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Check In / Sector Scan Dialog */}
      <Dialog open={showScanDialog} onOpenChange={setShowScanDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-400" />
              Check In
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              {selectedCheckpoint?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Scan Status */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Scan Result</Label>
              <Select value={scanStatus} onValueChange={setScanStatus}>
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  <SelectItem value="SUCCESS">
                    <span className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Success - All OK
                    </span>
                  </SelectItem>
                  <SelectItem value="FAILED">
                    <span className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-400" />
                      Failed - Issue Found
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* GPS Location - Auto captured on open */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Location Verification</Label>
              <div className="p-3 bg-[#09090b] rounded border border-[#3f3f46]">
                {gpsLoading ? (
                  <div className="flex items-center gap-2 text-blue-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Capturing GPS location...</span>
                  </div>
                ) : gpsLocation ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-emerald-400">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-sm">Location captured (±{Math.round(gpsLocation.accuracy)}m)</span>
                    </div>
                    <p className="text-xs text-zinc-500 pl-6">
                      {gpsLocation.latitude.toFixed(6)}, {gpsLocation.longitude.toFixed(6)}
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={requestGPS}
                      className="text-xs text-zinc-400 hover:text-white h-auto p-0 pl-6"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Refresh Location
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={requestGPS}
                    className="border-zinc-600 w-full"
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Capture GPS
                  </Button>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Notes / Exception Details</Label>
              <Textarea
                value={scanNotes}
                onChange={(e) => setScanNotes(e.target.value)}
                placeholder={scanStatus === 'FAILED' ? 'Describe the issue found...' : 'Any observations (optional)...'}
                className="bg-[#09090b] border-[#3f3f46]"
              />
            </div>

            {/* Photo Upload */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Photos (up to 2)</Label>
              <div className="space-y-2">
                {/* Uploaded photos preview */}
                {scanPhotos.length > 0 && (
                  <div className="flex gap-2">
                    {scanPhotos.map((photo, idx) => (
                      <div key={idx} className="relative w-20 h-20">
                        <img 
                          src={photo.url} 
                          alt={`Photo ${idx + 1}`}
                          className="w-full h-full object-cover rounded border border-[#3f3f46]"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(idx)}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload button */}
                {scanPhotos.length < 2 && (
                  <label className="flex items-center justify-center gap-2 p-3 bg-[#09090b] rounded border border-dashed border-[#3f3f46] cursor-pointer hover:border-blue-500 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      disabled={photoUploading}
                      className="hidden"
                    />
                    {photoUploading ? (
                      <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                    ) : (
                      <Camera className="w-5 h-5 text-zinc-400" />
                    )}
                    <span className="text-sm text-zinc-400">
                      {photoUploading ? 'Uploading...' : `Add Photo${scanPhotos.length === 0 ? 's' : ''}`}
                    </span>
                  </label>
                )}
              </div>
            </div>

            {/* Report Incident from Checkpoint - P0 Feature */}
            <div className="pt-2 border-t border-[#3f3f46]">
              <Button
                type="button"
                variant="outline"
                className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                onClick={() => {
                  setShowScanDialog(false);
                  setShowIncidentDialog(true);
                }}
              >
                <Shield className="w-4 h-4 mr-2" />
                Report Incident at this Sector
              </Button>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowScanDialog(false)}
              className="border-zinc-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleScanCheckpoint}
              className={scanStatus === 'SUCCESS' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-orange-500 hover:bg-orange-600'}
              disabled={scanLoading === selectedCheckpoint?.id}
              data-testid="confirm-scan-btn"
            >
              {scanLoading === selectedCheckpoint?.id ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Target className="w-4 h-4 mr-2" />
              )}
              Check Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Incident Modal */}
      {activePatrol && (
        <ReportIncidentModal
          isOpen={showIncidentDialog}
          onClose={() => setShowIncidentDialog(false)}
          patrol={activePatrol}
          api={api}
          onIncidentCreated={(incident) => {
            setPatrolIncidents(prev => [incident, ...prev]);
            setShowIncidentDialog(false);
          }}
        />
      )}

      {/* Patrols v2: Location Dialog */}
      <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-400" />
              {editingLocation ? 'Edit Location' : 'Add Location'}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              A location is a sub-area within a site (e.g., "Head Office", "Main Facility")
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Location Name <span className="text-red-400">*</span></Label>
              <Input
                value={locationForm.name}
                onChange={(e) => setLocationForm(prev => ({...prev, name: e.target.value}))}
                placeholder="e.g., Head Office, Main Entry, Car Park A"
                className="bg-[#09090b] border-[#3f3f46]"
                data-testid="location-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Address</Label>
              <Input
                value={locationForm.address}
                onChange={(e) => setLocationForm(prev => ({...prev, address: e.target.value}))}
                placeholder="e.g., 36 Honeysuckle Drive, Newcastle"
                className="bg-[#09090b] border-[#3f3f46]"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">GPS Coordinates</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={handleUseGpsForLocation}
                  className="text-blue-400 hover:text-blue-300 h-7 text-xs"
                >
                  <Crosshair className="w-3 h-3 mr-1" />
                  Use GPS
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={locationForm.gps_lat}
                  onChange={(e) => setLocationForm(prev => ({...prev, gps_lat: e.target.value}))}
                  placeholder="Latitude"
                  className="bg-[#09090b] border-[#3f3f46]"
                />
                <Input
                  value={locationForm.gps_lng}
                  onChange={(e) => setLocationForm(prev => ({...prev, gps_lng: e.target.value}))}
                  placeholder="Longitude"
                  className="bg-[#09090b] border-[#3f3f46]"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Geofence Radius (m)</Label>
              <Input
                type="number"
                value={locationForm.geofence_radius_m}
                onChange={(e) => setLocationForm(prev => ({...prev, geofence_radius_m: e.target.value}))}
                min={25}
                max={500}
                className="bg-[#09090b] border-[#3f3f46]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Description</Label>
              <Textarea
                value={locationForm.description}
                onChange={(e) => setLocationForm(prev => ({...prev, description: e.target.value}))}
                placeholder="Optional description..."
                className="bg-[#09090b] border-[#3f3f46]"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowLocationDialog(false)} className="border-zinc-600">
              Cancel
            </Button>
            <Button onClick={handleSaveLocation} className="bg-emerald-500 hover:bg-emerald-600" data-testid="save-location-btn">
              {editingLocation ? 'Update' : 'Create'} Location
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Patrols v2: Sector Dialog */}
      <Dialog open={showSectorDialog} onOpenChange={setShowSectorDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-400" />
              {editingSector ? 'Edit Sector' : 'Add Sector'}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              {selectedLocationForSector && (
                <>Adding sector to <strong>{selectedLocationForSector.name}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Sector Name <span className="text-red-400">*</span></Label>
              <Input
                value={sectorForm.name}
                onChange={(e) => setSectorForm(prev => ({...prev, name: e.target.value}))}
                placeholder="e.g., Main Entry, Rear Door, Weighbridge"
                className="bg-[#09090b] border-[#3f3f46]"
                data-testid="sector-name-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">GPS Coordinates</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={handleUseGpsForSector}
                  className="text-blue-400 hover:text-blue-300 h-7 text-xs"
                >
                  <Crosshair className="w-3 h-3 mr-1" />
                  Use GPS
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={sectorForm.gps_lat}
                  onChange={(e) => setSectorForm(prev => ({...prev, gps_lat: e.target.value}))}
                  placeholder="Latitude"
                  className="bg-[#09090b] border-[#3f3f46]"
                />
                <Input
                  value={sectorForm.gps_lng}
                  onChange={(e) => setSectorForm(prev => ({...prev, gps_lng: e.target.value}))}
                  placeholder="Longitude"
                  className="bg-[#09090b] border-[#3f3f46]"
                />
              </div>
              <p className="text-xs text-zinc-500">Optional: Set GPS if sector has a specific check-in point</p>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Geofence Radius (m)</Label>
              <Input
                type="number"
                value={sectorForm.geofence_radius_m}
                onChange={(e) => setSectorForm(prev => ({...prev, geofence_radius_m: e.target.value}))}
                min={25}
                max={250}
                className="bg-[#09090b] border-[#3f3f46]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Description</Label>
              <Textarea
                value={sectorForm.description}
                onChange={(e) => setSectorForm(prev => ({...prev, description: e.target.value}))}
                placeholder="Optional notes about this sector..."
                className="bg-[#09090b] border-[#3f3f46]"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSectorDialog(false)} className="border-zinc-600">
              Cancel
            </Button>
            <Button onClick={handleSaveSector} className="bg-blue-500 hover:bg-blue-600" data-testid="save-sector-btn">
              {editingSector ? 'Update' : 'Create'} Sector
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Patrol Route Dialog */}
      <Dialog open={showCreateRouteDialog} onOpenChange={setShowCreateRouteDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
              <Route className="w-5 h-5 text-blue-400" />
              Create Patrol Route
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Define a new patrol route for officers to follow
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Route Name <span className="text-red-400">*</span></Label>
              <Input
                value={createRouteForm.name}
                onChange={(e) => setCreateRouteForm(prev => ({...prev, name: e.target.value}))}
                placeholder="e.g., Night Patrol - Main Building"
                className="bg-[#09090b] border-[#3f3f46]"
                data-testid="create-route-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Site <span className="text-red-400">*</span></Label>
              <Select 
                value={createRouteForm.site_id} 
                onValueChange={(v) => setCreateRouteForm(prev => ({...prev, site_id: v}))}
              >
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="create-route-site-select">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  {sites.map(site => (
                    <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Duration (mins)</Label>
                <Input
                  type="number"
                  value={createRouteForm.estimated_duration_minutes}
                  onChange={(e) => setCreateRouteForm(prev => ({...prev, estimated_duration_minutes: e.target.value}))}
                  min={5}
                  max={480}
                  className="bg-[#09090b] border-[#3f3f46]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Frequency</Label>
                <Select 
                  value={createRouteForm.frequency} 
                  onValueChange={(v) => setCreateRouteForm(prev => ({...prev, frequency: v}))}
                >
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    <SelectItem value="once">Once</SelectItem>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="shift">Per Shift</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Description</Label>
              <Textarea
                value={createRouteForm.description}
                onChange={(e) => setCreateRouteForm(prev => ({...prev, description: e.target.value}))}
                placeholder="Optional notes about this patrol route..."
                className="bg-[#09090b] border-[#3f3f46]"
              />
            </div>
            <p className="text-xs text-zinc-500">
              After creating the route, use the Route Builder to add checkpoints and stops.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCreateRouteDialog(false)} className="border-zinc-600" disabled={routeSubmitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateRoute} 
              className="bg-blue-500 hover:bg-blue-600" 
              disabled={routeSubmitting}
              data-testid="save-route-btn"
            >
              {routeSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Route'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* "Next: Add Checkpoints" Dialog - shown after creating a route */}
      <Dialog open={showNextStepDialog} onOpenChange={setShowNextStepDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              Route Created!
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Your patrol route "{newlyCreatedRoute?.name}" has been created as a <span className="text-yellow-400">Draft</span>.
              Add checkpoints to make it active.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 bg-[#09090b] rounded border border-[#3f3f46] space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
              <span className="text-zinc-300">Routes need at least 1 checkpoint to be Active</span>
            </div>
            <p className="text-xs text-zinc-500">
              Officers cannot start patrol runs on Draft routes.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowNextStepDialog(false)} 
              className="border-zinc-600"
            >
              Skip for now
            </Button>
            <Button 
              className="bg-blue-500 hover:bg-blue-600"
              onClick={() => {
                setShowNextStepDialog(false);
                if (newlyCreatedRoute) {
                  openCheckpointManager(newlyCreatedRoute);
                }
              }}
              data-testid="add-checkpoints-next-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Checkpoints
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Route Checkpoint Manager */}
      <RouteCheckpointManager
        open={showCheckpointManager}
        onOpenChange={setShowCheckpointManager}
        route={selectedRouteForCheckpoints}
        onUpdate={fetchData}
      />
    </div>
  );
}
